import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Clock,
  FileText,
  Image,
  Code,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import { usePage } from '../hooks/useSEO';
import { ScoreCircle } from '../components/seo/ScoreCircle';
import { IssuesList } from '../components/seo/IssuesList';

export function PageDetailPage() {
  const { projectId, pageId } = useParams<{ projectId: string; pageId: string }>();
  const { page, loading, error } = usePage(projectId, pageId);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-900 mb-2">Page not found</h3>
          <p className="text-surface-500 mb-6">{error}</p>
          <Link to={`/projects/${projectId}`} className="btn-primary mx-auto">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const getUrlPath = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.pathname || '/';
    } catch {
      return url;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/projects/${projectId}`}
          className="text-sm text-surface-500 hover:text-surface-700 flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to Dashboard
        </Link>

        <div className="flex items-start gap-6">
          <ScoreCircle score={page.seoScore} size="lg" />

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-surface-900 line-clamp-2">
              {page.title || 'Untitled Page'}
            </h1>
            <a
              href={page.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1 mt-1"
            >
              <ExternalLink className="w-3 h-3" />
              {page.url}
            </a>

            <div className="flex items-center gap-4 mt-3 text-sm text-surface-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {page.loadTimeMs ? `${(page.loadTimeMs / 1000).toFixed(2)}s` : '-'}
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {page.wordCount.toLocaleString()} words
              </span>
              <span className="flex items-center gap-1">
                <Image className="w-4 h-4" />
                {page.totalImages} images
              </span>
              {page.httpStatus && (
                <span className={clsx(
                  'flex items-center gap-1',
                  page.httpStatus >= 200 && page.httpStatus < 300 ? 'text-green-600' : 'text-red-600'
                )}>
                  HTTP {page.httpStatus}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Issues */}
          {(page.issues.length > 0 || page.warnings.length > 0) && (
            <div className="card">
              <h2 className="font-semibold text-surface-900 mb-4">Issues & Warnings</h2>
              <IssuesList issues={[...page.issues, ...page.warnings]} />
            </div>
          )}

          {/* Meta Tags */}
          <div className="card">
            <h2 className="font-semibold text-surface-900 mb-4">Meta Tags</h2>
            <div className="space-y-4">
              <MetaField
                label="Title"
                value={page.title}
                length={page.titleLength}
                ideal={{ min: 30, max: 60 }}
              />
              <MetaField
                label="Description"
                value={page.metaDescription}
                length={page.metaDescriptionLength}
                ideal={{ min: 120, max: 160 }}
              />
              <MetaField label="Canonical" value={page.canonicalUrl} />
              <MetaField label="Robots" value={page.robotsMeta} />
              <MetaField label="Viewport" value={page.viewportContent} />
            </div>
          </div>

          {/* Open Graph */}
          <div className="card">
            <h2 className="font-semibold text-surface-900 mb-4">Open Graph</h2>
            <div className="space-y-4">
              <MetaField label="OG Title" value={page.ogTitle} />
              <MetaField label="OG Description" value={page.ogDescription} />
              <MetaField label="OG Image" value={page.ogImage} isUrl />
              <MetaField label="OG Type" value={page.ogType} />
            </div>
          </div>

          {/* Twitter Cards */}
          <div className="card">
            <h2 className="font-semibold text-surface-900 mb-4">Twitter Cards</h2>
            <div className="space-y-4">
              <MetaField label="Card Type" value={page.twitterCard} />
              <MetaField label="Title" value={page.twitterTitle} />
              <MetaField label="Description" value={page.twitterDescription} />
              <MetaField label="Image" value={page.twitterImage} isUrl />
            </div>
          </div>

          {/* Headings */}
          <div className="card">
            <h2 className="font-semibold text-surface-900 mb-4">Heading Structure</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-surface-700">H1 Tags</span>
                <span className={clsx(
                  'text-sm font-medium',
                  page.h1Count === 1 ? 'text-green-600' : page.h1Count === 0 ? 'text-red-600' : 'text-yellow-600'
                )}>
                  {page.h1Count}
                </span>
              </div>
              {page.h1Text && page.h1Text.length > 0 && (
                <div className="pl-4 space-y-1">
                  {page.h1Text.map((h1, i) => (
                    <p key={i} className="text-sm text-surface-600 bg-surface-50 rounded px-2 py-1">
                      {h1}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-surface-700">H2 Tags</span>
                <span className="text-sm text-surface-600">{page.h2Count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-surface-700">H3 Tags</span>
                <span className="text-sm text-surface-600">{page.h3Count}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card">
            <h2 className="font-semibold text-surface-900 mb-4">Quick Stats</h2>
            <div className="space-y-3">
              <StatRow
                label="Content Size"
                value={page.contentSize ? `${(page.contentSize / 1024).toFixed(1)} KB` : '-'}
              />
              <StatRow label="Word Count" value={page.wordCount.toLocaleString()} />
              <StatRow
                label="Readability"
                value={page.readabilityScore !== undefined ? `${page.readabilityScore}/100` : '-'}
              />
              <StatRow label="Total Images" value={String(page.totalImages)} />
              <StatRow
                label="Images without Alt"
                value={String(page.imagesWithoutAlt)}
                warning={page.imagesWithoutAlt > 0}
              />
            </div>
          </div>

          {/* Schema Markup */}
          <div className="card">
            <h2 className="font-semibold text-surface-900 mb-4">Schema Markup</h2>
            {page.hasSchema ? (
              <div>
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Schema detected</span>
                </div>
                {page.schemaTypes && page.schemaTypes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {page.schemaTypes.map((type, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-surface-500">
                <XCircle className="w-4 h-4" />
                <span className="text-sm">No schema markup found</span>
              </div>
            )}
          </div>

          {/* Keywords */}
          {page.keywords && page.keywords.length > 0 && (
            <div className="card">
              <h2 className="font-semibold text-surface-900 mb-4">Top Keywords</h2>
              <div className="space-y-2">
                {page.keywords.slice(0, 10).map((kw, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-surface-700">{kw.keyword}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-surface-500">{kw.count}x</span>
                      <span className="text-xs text-surface-400">
                        ({kw.density.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sitemap Info */}
          {(page.lastmod || page.priority !== undefined || page.changefreq) && (
            <div className="card">
              <h2 className="font-semibold text-surface-900 mb-4">Sitemap Data</h2>
              <div className="space-y-3">
                {page.lastmod && (
                  <StatRow
                    label="Last Modified"
                    value={new Date(page.lastmod).toLocaleDateString()}
                  />
                )}
                {page.priority !== undefined && (
                  <StatRow label="Priority" value={String(page.priority)} />
                )}
                {page.changefreq && (
                  <StatRow label="Change Frequency" value={page.changefreq} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaField({
  label,
  value,
  length,
  ideal,
  isUrl,
}: {
  label: string;
  value?: string;
  length?: number;
  ideal?: { min: number; max: number };
  isUrl?: boolean;
}) {
  const isEmpty = !value || value.trim() === '';

  let lengthStatus: 'ok' | 'short' | 'long' | undefined;
  if (ideal && length) {
    if (length < ideal.min) lengthStatus = 'short';
    else if (length > ideal.max) lengthStatus = 'long';
    else lengthStatus = 'ok';
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-surface-700">{label}</span>
        {length !== undefined && (
          <span
            className={clsx(
              'text-xs',
              lengthStatus === 'ok' && 'text-green-600',
              lengthStatus === 'short' && 'text-yellow-600',
              lengthStatus === 'long' && 'text-red-600',
              !lengthStatus && 'text-surface-500'
            )}
          >
            {length} chars
            {ideal && ` (ideal: ${ideal.min}-${ideal.max})`}
          </span>
        )}
      </div>
      {isEmpty ? (
        <span className="text-sm text-red-500 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Not set
        </span>
      ) : isUrl ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-brand-600 hover:text-brand-700 break-all"
        >
          {value}
        </a>
      ) : (
        <p className="text-sm text-surface-600 bg-surface-50 rounded px-2 py-1 break-words">
          {value}
        </p>
      )}
    </div>
  );
}

function StatRow({
  label,
  value,
  warning,
}: {
  label: string;
  value: string;
  warning?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-surface-600">{label}</span>
      <span
        className={clsx(
          'text-sm font-medium',
          warning ? 'text-yellow-600' : 'text-surface-900'
        )}
      >
        {value}
      </span>
    </div>
  );
}
